import { useState, type ReactNode } from 'react';
import { logout } from './api';
import { useSession, useTenant } from './app';
import { navigate, onLinkClick } from './router';

/** Interner Link ohne Neuladen. */
export function Link({ href, className, children, ...rest }: { href: string; className?: string; children: ReactNode } & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'className' | 'children'>) {
  return (
    <a href={href} className={className} onClick={onLinkClick} {...rest}>
      {children}
    </a>
  );
}

/** Kopfzeile und Fuß für alle Seiten – Logo oder Name des Anbieters, Navigation, Rechtstexte. */
export function Shell({ children }: { children: ReactNode }) {
  const tenant = useTenant();
  const session = useSession();
  const logo = document.documentElement.dataset['scheme'] === 'dark' ? (tenant.theme.logoDarkUrl ?? tenant.theme.logoUrl) : tenant.theme.logoUrl;
  // Ein Bildlogo ohne Schriftzug (eher quadratisch) bekommt den Namen daneben – eine Wortmarke steht allein.
  const [compactLogo, setCompactLogo] = useState(false);

  async function signOut() {
    await logout().catch(() => undefined);
    await session.refresh();
    navigate('/anmelden');
  }

  return (
    <>
      <header className="top">
        <div className="top__inner">
          <Link href="/" className="top__brand" aria-label={tenant.name}>
            {logo ? (
              <>
                <img src={logo} alt={tenant.name} className={`top__logo${compactLogo ? ' top__logo--compact' : ''}`} onLoad={(e) => setCompactLogo(e.currentTarget.naturalWidth / e.currentTarget.naturalHeight < 2.2)} />
                {compactLogo && <Wordmark name={tenant.name} />}
              </>
            ) : (
              <Wordmark name={tenant.name} />
            )}
          </Link>
          <nav className="top__nav">
            <Link href="/" className="top__link">
              Bibliothek
            </Link>
            {session.loggedIn ? (
              <>
                <Link href="/konto" className="top__link">
                  Konto
                </Link>
                <button type="button" className="top__link top__link--button" onClick={() => void signOut()}>
                  Abmelden
                </button>
              </>
            ) : (
              <Link href="/anmelden" className="top__link">
                Anmelden
              </Link>
            )}
          </nav>
        </div>
      </header>
      {children}
      <footer className="foot">
        <div className="foot__inner">
          <span>{tenant.name}</span>
          <span className="foot__links">
            <Link href="/impressum">Impressum</Link>
            <Link href="/datenschutz">Datenschutz</Link>
          </span>
        </div>
      </footer>
    </>
  );
}

/** Name des Anbieters als Wortmarke, wenn kein Logo hochgeladen ist: erstes Wort stark, Rest leicht. */
export function Wordmark({ name }: { name: string }) {
  const [first, ...rest] = name.split(' ');
  return (
    <span className="wordmark">
      <strong>{first}</strong>
      {rest.length > 0 && <span className="wordmark__rest">{rest.join(' ')}</span>}
    </span>
  );
}

/** Kleine Zeile in Versalien mit Akzentfarbe – über Überschriften. */
export function Kicker({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={`kicker${className ? ` ${className}` : ''}`}>{children}</span>;
}

/** Zweizeiliger Titel: Zeilenumbruch im Text trennt, die zweite Zeile wird zur Akzentzeile (wie in der Vorlage). */
export function TwoLineTitle({ title, as: Tag = 'h1', className }: { title: string; as?: 'h1' | 'h2' | 'h3'; className?: string }) {
  const [first, ...rest] = title.split('\n');
  return (
    <Tag className={className}>
      {first}
      {rest.length > 0 && (
        <>
          <br />
          <span className="accent">{rest.join(' ')}</span>
        </>
      )}
    </Tag>
  );
}

export function ProgressBar({ percent, className, thin }: { percent: number; className?: string; thin?: boolean }) {
  return (
    <div className={`bar${thin ? ' bar--thin' : ''}${className ? ` ${className}` : ''}`} role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}>
      <div className="bar__fill" style={{ width: `${Math.max(0, Math.min(100, percent))}%` }} />
    </div>
  );
}

/**
 * Bild einer Kachel: das Vorschaubild des Videos, sonst eine ruhige Fläche in den Farben des Anbieters,
 * deren Verlauf sich je Nummer leicht dreht – so sieht nicht jede Kachel gleich aus.
 */
export function Thumb({ src, seed, children, className }: { src: string | null; seed: number; children?: ReactNode; className?: string }) {
  const angle = 120 + ((seed * 47) % 120);
  const shift = (seed * 13) % 40;
  return (
    <div className={`thumb${className ? ` ${className}` : ''}`} style={src ? { backgroundImage: `url(${src})` } : { backgroundImage: `linear-gradient(${angle}deg, var(--thumb-a) ${shift}%, var(--thumb-b) 100%)` }}>
      {!src && <div className="thumb__grain" />}
      {children}
    </div>
  );
}

export function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
      <path d="M4 10.5l4 4 8-9" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function LockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
      <rect x="4" y="9" width="12" height="9" rx="2" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="M7 9V6.5a3 3 0 0 1 6 0V9" fill="none" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

export function PlayIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
      <path d="M8 5.5v13l11-6.5z" fill="currentColor" />
    </svg>
  );
}
